import { Box, Button, Divider, Flex } from "@chakra-ui/react";
import useAssets from "hooks/useAssets";
import useTranslate from "hooks/useTranslate";
import useHoneyInBears from "hooks/useHoneyInBears";
import useHoneyInJars from "hooks/useHoneyInJars";
import { BigNumber } from "ethers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatEther } from "ethers/lib/utils";
import useConnectedAccount from "hooks/useConnectedAccount";
import { getShortenedAddress } from "utils/walletUtils";
import PayFormSlider from "components/PayFormSlider";
import TinyHeading from "components/TinyHeading";
import ScrollableArea from "components/ScrollableArea";
import { formatEtherBalance } from "utils/numberUtils";
import useTraitSales from "hooks/useTraitSales";
import usePurchaseTraits from "hooks/usePurchaseTraits";

const BEAR_PREFIX = "fancyBear_";
const JAR_PREFIX = "honeyJar_";

export type PayFormValue = {
  amount: BigNumber;
  isLocked?: boolean;
};

export type PayFormValues = Record<string, PayFormValue>;

type PayFormProps = {
  tokenId: number;
  newTraits: number[];
};

const updateCopyByKey = (
  valueKey: string,
  newValue: PayFormValue,
  originalValues: PayFormValues
) => ({
  ...originalValues,
  [valueKey]: {
    ...(originalValues[valueKey] || {
      isLocked: false,
      amount: BigNumber.from("0"),
    }),
    ...newValue,
  },
});

const distributeValues = (
  currentValues: PayFormValues,
  totalPrice: BigNumber,
  valueKey?: string,
  valueAmount?: BigNumber
) => {
  const currentAmount = valueAmount ? valueAmount : BigNumber.from("0");
  const valuesToUpdate = Object.entries(currentValues).filter(
    ([entryKey]) => entryKey !== valueKey
  );
  const otherValuesAmount = valuesToUpdate
    .reduce(
      (sum: BigNumber, [, { amount }]) => sum.add(amount),
      BigNumber.from("0")
    )
    .add(currentAmount);
  const unlockedValues = valuesToUpdate.filter(([, { isLocked }]) => !isLocked);

  let values: PayFormValues = { ...currentValues };
  let amountToDistribute = totalPrice.sub(otherValuesAmount);

  if (amountToDistribute.lt(0)) {
    while (amountToDistribute.lt(0) && unlockedValues.length > 0) {
      const [entryKey, entryValue] = unlockedValues.pop()!;
      const bnAmount = BigNumber.from(entryValue.amount);
      const distributedAmount = bnAmount.gt(amountToDistribute.abs())
        ? amountToDistribute.abs()
        : bnAmount;

      amountToDistribute = amountToDistribute.add(distributedAmount);

      values = updateCopyByKey(
        entryKey,
        {
          amount: bnAmount.sub(distributedAmount),
        },
        values
      );
    }
  }

  if (valueKey) {
    values = updateCopyByKey(
      valueKey,
      {
        amount: amountToDistribute.lt(0)
          ? currentAmount.add(amountToDistribute)
          : currentAmount,
      },
      values
    );
  }

  return values;
};

const PayForm = ({ tokenId, newTraits }: PayFormProps) => {
  const translate = useTranslate();
  const [address] = useConnectedAccount();
  const { traitSalesMap } = useTraitSales();
  const { honeyBalance } = useAssets();
  const honeyInBears = useHoneyInBears();
  const honeyInJars = useHoneyInJars();

  const totalPrice = useMemo(
    () =>
      newTraits.reduce(
        (prev: BigNumber, tokenId) =>
          prev.add(traitSalesMap?.[tokenId].price || "0"),
        BigNumber.from("0")
      ),
    [newTraits, traitSalesMap]
  );

  const [values, setValues] = useState<PayFormValues>({});

  const currentTotalValue = useMemo(
    () =>
      Object.values(values).reduce(
        (sum: BigNumber, curr) => sum.add(curr.amount),
        BigNumber.from("0")
      ),
    [values]
  );

  const currentTotalPercentage =
    (parseInt(formatEther(currentTotalValue)) /
      parseInt(formatEther(totalPrice))) *
    100;

  const isFull = currentTotalPercentage === 100;

  const handleChange = useCallback(
    (valueKey: keyof PayFormValues, value: BigNumber) => {
      setValues(distributeValues(values, totalPrice, valueKey, value));
    },
    [values, totalPrice]
  );

  const handleLockToggle = useCallback(
    (key: keyof PayFormValues, isLocked?: boolean) => {
      setValues({
        ...values,
        [key]: {
          ...(values[key] || { amount: BigNumber.from("0") }),
          isLocked,
        },
      });
    },
    [values]
  );

  const purchaseData = useMemo(() => {
    if (tokenId && honeyInBears && honeyInJars && values) {
      const jars = Object.keys(honeyInJars)
        .map((tokenId) => ({
          tokenId: BigNumber.from(tokenId),
          amount:
            values[`${JAR_PREFIX}${tokenId}`]?.amount || BigNumber.from(0),
        }))
        .filter(({ amount }) => !BigNumber.from(amount).isZero());
      const amountForBar = values[`${BEAR_PREFIX}${tokenId}`]?.amount;

      return {
        amountToSpendFromWallet: values.wallet?.amount,
        ...(amountForBar && !BigNumber.from(amountForBar).isZero()
          ? {
              fancyBear: [tokenId],
              amountToSpendFromBear: [amountForBar],
            }
          : {
              fancyBear: [],
              amountToSpendFromBear: [],
            }),
        honeyJars: jars.map(({ tokenId }) => tokenId),
        amountToSpendFromHoneyJars: jars.map(({ amount }) => amount),
        traitTokenIds: newTraits,
        // TODO:
        amountPerTrait: newTraits.map(() => BigNumber.from("1")),
      };
    }
  }, [tokenId, honeyInBears, honeyInJars, values, newTraits]);
  const [purchase, isPurchasing] = usePurchaseTraits(purchaseData);

  useEffect(() => {
    if (
      !Object.keys(values).length &&
      honeyBalance &&
      honeyInBears &&
      honeyInJars &&
      tokenId
    ) {
      const initialValues = {
        wallet: {
          amount: honeyBalance,
          isLocked: false,
        },
        [`${BEAR_PREFIX}${tokenId}`]: {
          amount: honeyInBears[tokenId] || BigNumber.from("0"),
          isLocked: false,
        },
        ...Object.entries(honeyInJars).reduce(
          (prev, [tokenId, amount]) => ({
            ...prev,
            [`${JAR_PREFIX}${tokenId}`]: {
              amount,
              isLocked: false,
            },
          }),
          {}
        ),
      };

      setValues(distributeValues(initialValues, totalPrice));
    }
  }, [tokenId, values, totalPrice, honeyBalance, honeyInBears, honeyInJars]);

  return (
    <>
      {address && honeyBalance && !honeyBalance.isZero() && (
        <>
          <TinyHeading mb="3" mx="6">
            {translate("pay:wallet:title")}
          </TinyHeading>

          <PayFormSlider
            valueKey="wallet"
            values={values}
            max={honeyBalance}
            label={getShortenedAddress(address, 3)}
            onChange={handleChange}
            onLockToggle={handleLockToggle}
          />
        </>
      )}

      <Divider w="full" />

      {honeyInBears?.[tokenId] && !honeyInBears[tokenId].isZero() && (
        <>
          <TinyHeading my="3" mx="6">
            {translate("pay:fancyBears:title")}
          </TinyHeading>

          <PayFormSlider
            valueKey={`${BEAR_PREFIX}${tokenId}`}
            values={values}
            max={honeyInBears[tokenId]}
            label={`#${tokenId}`}
            onChange={handleChange}
            onLockToggle={handleLockToggle}
          />
        </>
      )}

      {honeyInJars && (
        <Flex
          direction="column"
          maxH="full"
          overflow="hidden"
          justify="stretch"
        >
          <TinyHeading my="3" mx="6">
            {translate("pay:honeyJars:title")}
          </TinyHeading>

          <ScrollableArea>
            {Object.entries(honeyInJars)
              .filter(([, honeyAmount]) => !honeyAmount.isZero())
              .map(([tokenId, honeyAmount]) => (
                <PayFormSlider
                  key={tokenId}
                  valueKey={`${JAR_PREFIX}${tokenId}`}
                  values={values}
                  max={honeyAmount || BigNumber.from("0")}
                  label={`#${tokenId}`}
                  onChange={handleChange}
                  onLockToggle={handleLockToggle}
                />
              ))}
          </ScrollableArea>
        </Flex>
      )}

      <Box mt="4" position="relative" mx="8">
        <Box
          position="absolute"
          top="0"
          left="0"
          bottom="0"
          width={`${currentTotalPercentage}%`}
          bg="secondary.500"
          transition="all 0.15s"
        ></Box>

        <Button
          w="full"
          colorScheme="secondary"
          onClick={() => purchase()}
          isDisabled={!isFull}
          isLoading={isPurchasing}
        >
          {translate("pay:submit", { price: formatEtherBalance(totalPrice) })}
        </Button>
      </Box>
    </>
  );
};

export default PayForm;
